/**
 * Utility functions for working with course tree data
 */

import { parseSubcourseOrder } from './subcourseSort';

const compareSubcoursesByOrder = (a, b) => {
  const orderA = parseSubcourseOrder(a.order) ?? Number.MAX_SAFE_INTEGER;
  const orderB = parseSubcourseOrder(b.order) ?? Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;
  return (a.code || '').localeCompare(b.code || '');
};

export const getSubcourseLevelCode = (subcourse) =>
  subcourse?.['subcourse-code'] ||
  subcourse?.subcourseCode ||
  subcourse?.code ||
  subcourse?.name ||
  subcourse?.title ||
  subcourse?.level ||
  subcourse?.levelCode ||
  null;

/**
 * Return the first subcourse sorted by `order` (lowest first) across all courses in the tree.
 */
export const getFirstSubcourseByOrder = (tree) => {
  const courses = extractCoursesFromTree(tree);
  const collected = [];

  for (const course of courses) {
    if (!course.subcourses || !Array.isArray(course.subcourses)) continue;

    for (const subcourse of course.subcourses) {
      const code = getSubcourseLevelCode(subcourse);
      if (!code) continue;

      collected.push({
        ...subcourse,
        id: subcourse._id || subcourse.id,
        code,
        order: subcourse.order,
      });
    }
  }

  if (collected.length === 0) return null;

  collected.sort(compareSubcoursesByOrder);

  return collected[0];
};

/**
 * Find subcourse by level in course tree
 * Handles various course tree structures
 */
export const findSubcourseByLevel = (tree, level) => {
  if (!tree || !level) return null;

  // Handle if tree is an array
  let courses = [];
  if (Array.isArray(tree)) {
    courses = tree;
  } else if (tree && Array.isArray(tree.courses)) {
    courses = tree.courses;
  } else if (tree && typeof tree === 'object') {
    // Try to find courses in various possible structures
    const possibleKeys = ['courses', 'data', 'courseTree', 'tree'];
    for (const key of possibleKeys) {
      if (Array.isArray(tree[key])) {
        courses = tree[key];
        break;
      }
    }
    
    // If still no array, try to iterate object values
    if (courses.length === 0) {
      const values = Object.values(tree);
      const allArrays = values.filter(v => Array.isArray(v));
      if (allArrays.length > 0) {
        courses = allArrays.flat();
      }
    }
  }
  
  if (courses.length === 0) {
    return null;
  }

  // Loop through all courses
  for (const course of courses) {
    if (!course.subcourses || !Array.isArray(course.subcourses)) {
      continue;
    }

    // Loop through all subcourses in this course
    for (const subcourse of course.subcourses) {
      // Try multiple ways to match subcourse with level
      const subcourseCode = subcourse['subcourse-code'] || subcourse.subcourseCode || subcourse.code;
      const subcourseName = subcourse.name || subcourse.title;
      const subcourseLevel = subcourse.level || subcourse.levelCode;

      // Match by code, name, or level field
      const matchValue = subcourseCode || subcourseName || subcourseLevel;
      if (matchValue && matchValue.toString().toLowerCase() === level.toLowerCase()) {
        return {
          ...subcourse,
          id: subcourse._id || subcourse.id
        };
      }
    }
  }

  return null;
};

/**
 * Extract user stats from subcourse
 */
export const extractUserStats = (subcourse) => {
  if (!subcourse) {
    return {
      allStats: {},
      quickStats: {},
      writingStats: {},
      speakingStats: {}
    };
  }

  const userStats = subcourse['user-stats'] || {};
  return {
    allStats: userStats['all-stats'] || {},
    quickStats: userStats['quick-stats'] || {},
    writingStats: userStats['writing-stats'] || {},
    speakingStats: userStats['speaking-stats'] || {}
  };
};

/**
 * Extract courses array from course tree
 * Handles various course tree structures
 */
export const extractCoursesFromTree = (tree) => {
  if (!tree) return [];

  if (Array.isArray(tree)) {
    return tree;
  }
  
  if (tree && Array.isArray(tree.courses)) {
    return tree.courses;
  }
  
  if (tree && typeof tree === 'object') {
    // Try to find courses in various possible structures
    const possibleKeys = ['courses', 'data', 'courseTree', 'tree'];
    for (const key of possibleKeys) {
      if (Array.isArray(tree[key])) {
        return tree[key];
      }
    }
    
    // If still no array, try to iterate object values
    const values = Object.values(tree);
    const allArrays = values.filter(v => Array.isArray(v));
    if (allArrays.length > 0) {
      return allArrays.flat();
    }
  }
  
  return [];
};

/**
 * Get first course ID from course tree
 */
export const getFirstCourseId = (tree) => {
  const courses = extractCoursesFromTree(tree);
  if (courses.length > 0) {
    const firstCourse = courses[0];
    return firstCourse._id || firstCourse.id || null;
  }
  return null;
};

/**
 * Find subcourse by ID in course tree
 */
export const findSubcourseById = (tree, subcourseId) => {
  if (!tree || !subcourseId) return null;

  const courses = extractCoursesFromTree(tree);

  for (const course of courses) {
    if (!course.subcourses || !Array.isArray(course.subcourses)) {
      continue;
    }

    for (const subcourse of course.subcourses) {
      const subcourseIdToMatch = subcourse._id || subcourse.id;
      if (String(subcourseIdToMatch) === String(subcourseId)) {
        return {
          ...subcourse,
          id: subcourseIdToMatch,
        };
      }
    }
  }

  return null;
};

/**
 * Resolve subcourse when courseTreeAtom is null or filtered
 */
export const findSubcourseByIdWithFallback = (subcourseId, courseTree, courseTreeBaseline) => {
  if (!subcourseId) return null;
  if (courseTree) {
    const fromPrimary = findSubcourseById(courseTree, subcourseId);
    if (fromPrimary) return fromPrimary;
  }
  if (courseTreeBaseline) {
    return findSubcourseById(courseTreeBaseline, subcourseId);
  }
  return null;
};

/**
 * Find course ID by subcourse ID
 * Returns the parent course ID for a given subcourse ID
 */
export const findCourseIdBySubcourseId = (tree, subcourseId) => {
  if (!tree || !subcourseId) return null;

  const courses = extractCoursesFromTree(tree);

  // Loop through all courses
  for (const course of courses) {
    if (!course.subcourses || !Array.isArray(course.subcourses)) {
      continue;
    }

    // Loop through all subcourses in this course
    for (const subcourse of course.subcourses) {
      const subcourseIdToMatch = subcourse._id || subcourse.id;
      if (subcourseIdToMatch === subcourseId) {
        // Found the subcourse, return its parent course id
        return course._id || course.id || null;
      }
    }
  }

  return null;
};

/**
 * Find lang-code by subcourse ID
 * Returns the parent course's lang-code for a given subcourse ID
 */
export const findLangCodeBySubcourseId = (tree, subcourseId) => {
  if (!tree || !subcourseId) return null;

  const courses = extractCoursesFromTree(tree);

  // Loop through all courses
  for (const course of courses) {
    if (!course.subcourses || !Array.isArray(course.subcourses)) {
      continue;
    }

    // Loop through all subcourses in this course
    for (const subcourse of course.subcourses) {
      const subcourseIdToMatch = subcourse._id || subcourse.id;
      if (String(subcourseIdToMatch) === String(subcourseId)) {
        // Found the subcourse, return its parent course's lang-code
        return course['lang-code'] || null;
      }
    }
  }

  return null;
};

/**
 * Resolve lang-code when courseTreeAtom is still null (e.g. full reload on practice page)
 * or the primary tree is a filtered view that omits the subcourse.
 * Tries the in-memory course tree first, then the baseline snapshot (localStorage).
 */
export const findLangCodeBySubcourseIdWithFallback = (subcourseId, courseTree, courseTreeBaseline) => {
  if (!subcourseId) return null;
  if (courseTree) {
    const fromPrimary = findLangCodeBySubcourseId(courseTree, subcourseId);
    if (fromPrimary) return fromPrimary;
  }
  if (courseTreeBaseline) {
    return findLangCodeBySubcourseId(courseTreeBaseline, subcourseId);
  }
  return null;
};

/**
 * Aggregate stats from all subcourses in course tree
 * Returns average level and progress across all subcourses
 */
export const aggregateSubcourseStats = (tree) => {
  const defaultStats = {
    allStats: { level: 0, progress: 0 },
    quickStats: { level: 0, progress: 0 },
    writingStats: { level: 0, progress: 0 },
    speakingStats: { level: 0, progress: 0 }
  };

  if (!tree) {
    return defaultStats;
  }

  const courses = extractCoursesFromTree(tree);
  
  // Aggregate stats from all subcourses
  let totalAllLevel = 0;
  let totalAllProgress = 0;
  let totalQuickLevel = 0;
  let totalQuickProgress = 0;
  let totalWritingLevel = 0;
  let totalWritingProgress = 0;
  let totalSpeakingLevel = 0;
  let totalSpeakingProgress = 0;
  let subcourseCount = 0;

  for (const course of courses) {
    if (!course.subcourses || !Array.isArray(course.subcourses)) {
      continue;
    }

    for (const subcourse of course.subcourses) {
      const userStats = subcourse['user-stats'];
      if (userStats) {
        const allStats = userStats['all-stats'] || {};
        const quickStats = userStats['quick-stats'] || {};
        const writingStats = userStats['writing-stats'] || {};
        const speakingStats = userStats['speaking-stats'] || {};

        totalAllLevel += allStats.level || 0;
        totalAllProgress += allStats.progress || 0;
        totalQuickLevel += quickStats.level || 0;
        totalQuickProgress += quickStats.progress || 0;
        totalWritingLevel += writingStats.level || 0;
        totalWritingProgress += writingStats.progress || 0;
        totalSpeakingLevel += speakingStats.level || 0;
        totalSpeakingProgress += speakingStats.progress || 0;
        subcourseCount++;
      }
    }
  }

  if (subcourseCount === 0) {
    return defaultStats;
  }

  return {
    allStats: {
      level: Math.round(totalAllLevel / subcourseCount),
      progress: Math.round(totalAllProgress / subcourseCount)
    },
    quickStats: {
      level: Math.round(totalQuickLevel / subcourseCount),
      progress: Math.round(totalQuickProgress / subcourseCount)
    },
    writingStats: {
      level: Math.round(totalWritingLevel / subcourseCount),
      progress: Math.round(totalWritingProgress / subcourseCount)
    },
    speakingStats: {
      level: Math.round(totalSpeakingLevel / subcourseCount),
      progress: Math.round(totalSpeakingProgress / subcourseCount)
    }
  };
};

